import { useEffect, useState } from 'react'
import { useLicenseEntitlements } from '@/hooks/useLicenseEntitlements'
import { AlertCircle, Loader2 } from 'lucide-react'
import { StatCard } from '@theseus/shared-ui/account'

export function StatsCards() {
  const { entitlements, loading, error, refetch } = useLicenseEntitlements()
  const [selectedLicenseId, setSelectedLicenseId] = useState<string | null>(null)
  const selected = entitlements.find((item) => item.license_id === selectedLicenseId) ?? entitlements[0]

  useEffect(() => {
    if (selectedLicenseId && !entitlements.some((item) => item.license_id === selectedLicenseId)) {
      setSelectedLicenseId(null)
    }
  }, [entitlements, selectedLicenseId])

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="mt-0.5 h-4 w-4 text-destructive" />
          <div>
            <p className="text-sm text-foreground">License information unavailable</p>
            <button className="mt-1 text-xs text-primary hover:text-primary/80" onClick={() => void refetch()}>
              Try again
            </button>
          </div>
        </div>
      </div>
    )
  }

  const isMvps = selected?.license_tier === 'LIC-MVPS'
  const annualQuota = selected?.annual_quota_km2 ?? 0
  const usedQuotaKm2 = selected?.used_km2 ?? 0
  const quotaProgress = annualQuota > 0 ? Math.min(100, (usedQuotaKm2 / annualQuota) * 100) : 0

  const loaderSlot = (
    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
  )

  return (
    <div className="space-y-3">
      {entitlements.length > 1 && (
        <label className="block max-w-sm text-xs text-muted-foreground">
          License context
          <select
            className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
            value={selected?.license_id ?? ''}
            onChange={(event) => setSelectedLicenseId(event.target.value)}
          >
            {entitlements.map((item) => (
              <option key={item.license_id} value={item.license_id}>
                {item.scope === 'organization' ? item.org_name : 'Personal'} · {item.license_tier}
              </option>
            ))}
          </select>
        </label>
      )}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <StatCard
          label="License"
          value={selected?.license_tier ?? 'No active license'}
          subValue={selected && !isMvps ? `${selected.active_devices}/${selected.total_devices} devices activated` : undefined}
          loading={loading}
          loadingSlot={loaderSlot}
        />
        <StatCard
          label="Map quota"
          value={selected ? (isMvps ? 'Unlimited' : `${usedQuotaKm2.toFixed(1)} km²`) : '—'}
          subValue={selected && !isMvps ? `of ${annualQuota.toLocaleString()} km² annual quota` : undefined}
          loading={loading}
          progress={selected && !isMvps ? quotaProgress : undefined}
          showProgress={!!selected && !isMvps}
          loadingSlot={loaderSlot}
        />
        <StatCard
          label="Maps generated"
          value={(selected?.maps_generated_this_year ?? 0).toString()}
          subValue="this year"
          loading={loading}
          loadingSlot={loaderSlot}
        />
      </div>
    </div>
  )
}
